import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useEffect } from 'react';

interface DebouncedSearchBarProps {
  placeholder?: string;
  onSearch?: (value: string) => void;
  debounceMs?: number;
  initialValue?: string;
  value?: string;
  className?: string;
  width?: string;
}

export default function DebouncedSearchBar({
  placeholder = 'Search...',
  onSearch,
  debounceMs = 500,
  initialValue = '',
  className = '',
  width = '100%',
}: DebouncedSearchBarProps) {
  const [searchTerm, setSearchTerm] = useState(initialValue);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs, onSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div
      style={{ width: width }}
      className={`flex items-center py-2 px-3 flex-row gap-4 flex-1 rounded-lg h-[44px] overflow-hidden bg-input-active-fill border-0.5 border-input-active-border ${className}`}
    >
      <div className="text-input-inactive-placeholder">
        <Search className="w-4 h-4" />
      </div>
      <input
        placeholder={placeholder}
        className="border-transparent text-sm w-full h-full bg-transparent placeholder:text-input-inactive-placeholder placeholder:text-sm outline-none"
        value={searchTerm}
        onChange={handleChange}
      />
      {searchTerm && (
        <div
          onClick={() => {
            setSearchTerm('');
          }}
          className="cursor-pointer hover:opacity-80"
        >
          <Icon icon={'famicons:close-outline'} fontSize={20} />
        </div>
      )}
    </div>
  );
}

function Search({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.66732 13.9999C11.1651 13.9999 14.0007 11.1644 14.0007 7.66659C14.0007 4.16878 11.1651 1.33325 7.66732 1.33325C4.16951 1.33325 1.33398 4.16878 1.33398 7.66659C1.33398 11.1644 4.16951 13.9999 7.66732 13.9999Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14.6673 14.6666L13.334 13.3333"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function ManageTeamIcon() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.99967 10C12.3009 10 14.1663 8.13454 14.1663 5.83335C14.1663 3.53217 12.3009 1.66669 9.99967 1.66669C7.69849 1.66669 5.83301 3.53217 5.83301 5.83335C5.83301 8.13454 7.69849 10 9.99967 10Z"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M2.8418 18.3333C2.8418 15.1083 6.05013 12.5 10.0001 12.5C10.8001 12.5 11.5751 12.6083 12.3001 12.8083"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M18.3337 15C18.3337 15.2667 18.3003 15.525 18.2337 15.775C18.1587 16.1083 18.0253 16.4334 17.8503 16.7167C17.2753 17.6834 16.217 18.3334 15.0003 18.3334C14.142 18.3334 13.367 18.0083 12.7837 17.475C12.5337 17.2583 12.317 17 12.1503 16.7167C11.842 16.2167 11.667 15.625 11.667 15C11.667 14.1 12.0253 13.275 12.6087 12.675C13.217 12.05 14.067 11.6667 15.0003 11.6667C15.9837 11.6667 16.8753 12.0917 17.4753 12.775C18.0087 13.3667 18.3337 14.15 18.3337 15Z"
        stroke="currentColor"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16.2411 14.9833H13.7578"
        stroke="currentColor"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M15 13.7667V16.2583"
        stroke="currentColor"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
